/**
 * 从本地存储读取权限数据到Vuex
 */
import store from '@/store'
import Vue from 'vue'
import { sk } from '@/config'

/**
 * 检查 store 是否已完全初始化
 */
function isStoreReady() {
  try {
    // 检查 store 对象是否存在
    if (!store || typeof store.dispatch !== 'function') {
      return false
    }
    // 检查是否有 state（store 初始化后会有 state）
    if (!store.state) {
      return false
    }
    // 检查 permission 模块是否已注册
    if (store.state.permission === undefined) {
      return false
    }
    return true
  } catch (e) {
    return false
  }
}

/**
 * 从localStorage读取用户权限并设置到Vuex
 */
export function loadPermissionsFromStorage() {
  // 如果 store 未初始化，直接返回，不打印警告
  if (!isStoreReady()) {
    return []
  }
  
  try {
    // 优先检查userModel，因为权限数据在这里
    const userModel = localStorage.getItem(sk('user_model'))
    
    // 只有当 userModel 存在且不为空时才解析
    // 注意：localStorage.getItem 可能返回 null（键不存在）或字符串 "null"
    if (userModel && userModel.trim() !== 'null') {
      try {
        const modelData = JSON.parse(userModel)
        
        // 关键检查：确保 modelData 不是 null，然后再访问属性
        // JSON.parse(null) 或 JSON.parse("null") 都会返回 null，访问 null.rolePermissionList 会报错
        if (modelData !== null && modelData !== undefined && modelData.rolePermissionList && Array.isArray(modelData.rolePermissionList)) {
          // 安全地调用 store.dispatch（已经在函数开头检查过 store 是否初始化）
          try {
            store.dispatch('permission/setPermissions', modelData.rolePermissionList)
          } catch (storeError) {
            // 静默失败（不打印警告，因为已经在函数开头检查过了）
          }
          
          // 安全地触发事件
          try {
            if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
              Vue.nextTick(() => {
                if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
                  Vue.prototype.$bus.$emit('permissions-loaded', modelData.rolePermissionList)
                }
              })
            }
          } catch (busError) {
            // $bus 可能还未初始化，静默失败
            if (process.env.NODE_ENV === 'development') {
              console.warn('事件总线未初始化，跳过事件发送:', busError)
            }
          }
          
          return modelData.rolePermissionList
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('解析user_model失败:', e)
        }
      }
    }
    
    // 备用检查其他localStorage键（如果user_model中没有找到权限）
    const uploadInfo = localStorage.getItem(sk('uploadInfo'))
    const userInfo = localStorage.getItem(sk('userInfo'))
    
    if (uploadInfo) {
      try {
        const data = JSON.parse(uploadInfo)
        // 检查 data 不为 null，且 user_model 存在且不为 null
        if (data && data !== null && data.user_model && data.user_model !== null && data.user_model.rolePermissionList && Array.isArray(data.user_model.rolePermissionList)) {
          // 安全地调用 store.dispatch（已经在函数开头检查过 store 是否初始化）
          try {
            store.dispatch('permission/setPermissions', data.user_model.rolePermissionList)
          } catch (storeError) {
            // 静默失败
          }
          
          // 安全地触发事件
          try {
            if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
              Vue.nextTick(() => {
                if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
                  Vue.prototype.$bus.$emit('permissions-loaded', data.user_model.rolePermissionList)
                }
              })
            }
          } catch (busError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('事件总线未初始化，跳过事件发送:', busError)
            }
          }
          
          return data.user_model.rolePermissionList
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('解析uploadInfo失败:', e)
        }
      }
    }
    
    if (userInfo) {
      try {
        const userData = JSON.parse(userInfo)
        // 检查 userData 不为 null
        if (userData && userData !== null && userData.rolePermissionList && Array.isArray(userData.rolePermissionList)) {
          // 安全地调用 store.dispatch（已经在函数开头检查过 store 是否初始化）
          try {
            store.dispatch('permission/setPermissions', userData.rolePermissionList)
          } catch (storeError) {
            // 静默失败
          }
          
          // 安全地触发事件
          try {
            if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
              Vue.nextTick(() => {
                if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
                  Vue.prototype.$bus.$emit('permissions-loaded', userData.rolePermissionList)
                }
              })
            }
          } catch (busError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('事件总线未初始化，跳过事件发送:', busError)
            }
          }
          
          return userData.rolePermissionList
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('解析userInfo失败:', e)
        }
      }
    }
  } catch (error) {
    // 捕获所有未预期的错误，防止阻塞路由守卫
    if (process.env.NODE_ENV === 'development') {
      console.error('从本地存储读取权限失败:', error)
    }
  }
  
  // 如果没有权限数据，设置空数组（安全地）
  // 注意：再次检查 store 是否就绪，因为可能在执行过程中 store 状态发生变化
  if (isStoreReady()) {
    try {
      store.dispatch('permission/setPermissions', [])
    } catch (storeError) {
      // 静默失败
    }
  }
  
  // 安全地触发事件
  try {
    if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
      Vue.nextTick(() => {
        if (Vue.prototype.$bus && typeof Vue.prototype.$bus.$emit === 'function') {
          Vue.prototype.$bus.$emit('permissions-loaded', [])
        }
      })
    }
  } catch (busError) {
    // 静默失败，不打印警告
  }
  
  return []
}

/**
 * 清除本地存储的权限数据
 */
export function clearPermissionsFromStorage() {
  try {
    // 清除Vuex权限（安全地）
    if (store && store.dispatch) {
      store.dispatch('permission/clearPermissions')
    }
    
    // 清除本地存储中的用户数据
    localStorage.removeItem(sk('uploadInfo'))
    if (process.env.NODE_ENV === 'development') {
      console.log('已清除本地存储的权限数据')
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('清除本地存储权限失败:', error)
    }
  }
}

/**
 * 检查是否有权限数据
 */
export function hasPermissionsInStorage() {
  try {
    // 优先检查 user_model
    const userModel = localStorage.getItem(sk('user_model'))
    if (userModel && userModel.trim() !== 'null') {
      try {
        const modelData = JSON.parse(userModel)
        if (modelData !== null && modelData !== undefined && modelData.rolePermissionList && Array.isArray(modelData.rolePermissionList) && modelData.rolePermissionList.length > 0) {
          return true
        }
      } catch (e) {
        // 解析失败，继续检查其他存储
      }
    }
    
    // 备用检查 uploadInfo
    const uploadInfo = localStorage.getItem(sk('uploadInfo'))
    if (uploadInfo) {
      try {
        const data = JSON.parse(uploadInfo)
        if (data && data !== null && data.user_model && data.user_model !== null && data.user_model.rolePermissionList && Array.isArray(data.user_model.rolePermissionList) && data.user_model.rolePermissionList.length > 0) {
          return true
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('检查本地存储权限失败:', error)
        }
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('检查本地存储权限失败:', error)
    }
  }
  return false
}
